const API_URL = process.env.REACT_APP_API_URL || '/api';

export interface HiredEquipmentSubmitRowPayload {
  /**
   * Flat-amount line. Sent with qty 1 / unit `$` / rate = amount; the server
   * re-normalizes and never trusts those three from the client.
   */
  lumpSum?: boolean | null;
  date: string;
  taskId: number;
  vendorId?: number | null;
  vendorName?: string | null;
  resourceLabel?: string | null;
  resourceId?: number | null;
  qty: number;
  unit?: string | null;
  rate: number;
  poNumber?: string | null;
  fuelSurchargeEligible?: boolean | null;
  vehicleType?: string | null;
  vendorMaterial?: boolean | null;
  vendorMaterialNotes?: string | null;
  overtimeEligible?: boolean | null;
  attachment?: {
    filename: string;
    contentType: string;
    size: number;
    url: string;
    key?: string;
  } | null;
  /** HE-F20 optional paper ticket. Not required. */
  ticketNumber?: string | null;
  /**
   * Per-line fuel surcharge $/hr override. Honored only if the caller has
   * fuel:custom_surcharge_override; otherwise the fuels table is used.
   */
  fuelSurchargeOverridePerHour?: number | null;
}

export interface HiredEquipmentSubmitPayload {
  anchor: {
    entryMethod: 'purchase_order' | 'subcontractor' | 'task';
    lockedVendorName?: string | null;
    lockedPoNumber?: string | null;
    serviceAreaId?: number | null;
  };
  rows: HiredEquipmentSubmitRowPayload[];
  internalComment?: string | null;
  /**
   * H1: Operations Coordinator "Submit" – the server creates the record Approved in one action
   * (and rolls it back if approval fails). Requires submission:hired_equipment_auto_approve.
   */
  submitAndApprove?: boolean;
  emailVendorPdf?: boolean;
  vendorEmail?: HeVendorEmailDraft;
  /** Present when this submit finishes a saved draft. The server removes that draft. */
  draftId?: string | null;
}

/** M6: a resource's hours on one date across all live submissions. */
export interface HeSameDayTotal {
  resourceId: number;
  date: string;
  resourceLabel: string;
  thisSubmission: number;
  otherSubmissions: number;
  total: number;
  level: 'warn' | 'block';
}

export interface HiredEquipmentDraftAnchor {
  entryMethod: 'purchase_order' | 'subcontractor' | 'task';
  lockedVendorId?: number;
  lockedVendorName?: string;
  lockedPoId?: number;
  lockedPoNumber?: string;
  lockedPoDescription?: string | null;
  lockedTaskId?: number;
  lockedTaskLabel?: string;
  serviceAreaId?: number | null;
  prePopulate?: boolean;
}

export interface HiredEquipmentDraftRow {
  clientId: string;
  date: string;
  vendorId: number | null;
  vendorName: string | null;
  poId: number | null;
  poNumber: string | null;
  taskId: number | null;
  taskLabel: string | null;
  resourceId: number | null;
  resourceLabel: string | null;
  qty: number | null;
  unit: string;
  rate: number | null;
  vendorMaterial: boolean;
  vendorMaterialNotes: string;
  fuelSurchargeEligible: boolean | null;
  fuelSurchargeOverridePerHour: number | null;
  overtimeEligible: boolean;
  resourceOvertimeEligible: boolean | null;
  vehicleType: string | null;
  attachment?: {
    filename: string;
    contentType: string;
    size: number;
    url: string;
    key?: string;
  } | null;
  ticketNumber: string | null;
  taskGroupId?: string;
  lumpSum: boolean;
  taskCreatedInSession?: boolean;
  cloneTaskOnSubmit?: boolean;
  lumpSumDescription?: string;
  /** V2-006: kept with the draft; stamped with author and time on submit. */
  lineComments?: Array<{ text: string; authorId?: number | null; authorName?: string | null; at?: string | null }>;
}

export interface HiredEquipmentDraftSummary {
  formId: string;
  createdAt: string;
  updatedAt: string;
  anchor: HiredEquipmentDraftAnchor;
  lineCount: number;
  totalValue: number;
}

export interface HiredEquipmentDraftRecord extends HiredEquipmentDraftSummary {
  rows: HiredEquipmentDraftRow[];
  internalComment: string;
}

export interface HiredEquipmentSubmitResult {
  formId: string;
  referenceNumber: string;
  status?: 'pending' | 'approved';
  sameDayWarnings?: HeSameDayTotal[];
}

/** An error from the HE API, keeping the status and body so callers can branch on a code. */
export class HiredEquipmentApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: Record<string, unknown> | null,
  ) {
    super(message);
    this.name = 'HiredEquipmentApiError';
  }
}

async function throwApiError(response: Response, fallback: string): Promise<never> {
  let body: Record<string, unknown> | null = null;
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    // ignore
  }
  // H9: only a 4xx message is written for users; a 5xx is always the generic text.
  const message =
    response.status < 500 && typeof body?.error === 'string' && body.error
      ? (body.error as string)
      : response.status === 403
        ? "You don't have permission to do that – contact your administrator."
        : `${fallback} – try again or contact support.`;
  throw new HiredEquipmentApiError(message, response.status, body);
}

export interface HeMyFormRow {
  formId: string;
  ticket: string;
  status: string;
  entryMethod: 'purchase_order' | 'subcontractor' | 'task';
  vendorName: string;
  poNumber: string | null;
  lineCount: number;
  totalValue: number;
  createdAt: string;
  createdByName: string;
  lastModifiedAt: string;
  lastModifiedByName: string | null;
}

/** H14: the logged-in user's recent HE submissions, from the server. */
export async function listMyHiredEquipmentForms(): Promise<HeMyFormRow[]> {
  const response = await fetch(`${API_URL}/hired-equipment/my-forms`, { credentials: 'include' });
  if (!response.ok) await throwApiError(response, 'Could not load your forms');
  return response.json() as Promise<HeMyFormRow[]>;
}

export interface HeLinePrice {
  rate: number | null;
  window: { rateId: number; rate: number; effectiveFrom: string; effectiveTo: string | null } | null;
  basis: 'own-rate' | 'component-sum' | 'none';
}

export interface HePriceLinesResult {
  prices: HeLinePrice[];
  quantityProblems: (string | null)[];
  sameDay: HeSameDayTotal[];
  /** H4: per-line compliance warnings on the line date. Absent from older servers. */
  complianceWarnings?: string[][];
}

/** C4/M6: the rate in effect on each line's date, and the same-day hours check. */
export async function priceHiredEquipmentLines(payload: {
  serviceAreaId: number | null | undefined;
  excludeFormId?: string | null;
  /** M4: the saved draft being finished – its "left as is" backdated rate choices apply. */
  draftId?: string | null;
  lines: {
    resourceId?: number | null;
    resourceLabel?: string | null;
    /** H4: the line's vendor, so a line without a resource still gets the vendor's documents checked. */
    vendorId?: number | null;
    /** The first line's task decides the service area of the work, as on submit. */
    taskId?: number | null;
    date: string;
    qty?: number | null;
    unit?: string | null;
    lumpSum?: boolean | null;
  }[];
}): Promise<HePriceLinesResult> {
  const response = await fetch(`${API_URL}/hired-equipment/price-lines`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) await throwApiError(response, 'Could not price the lines');
  return response.json() as Promise<HePriceLinesResult>;
}

export async function submitHiredEquipment(
  payload: HiredEquipmentSubmitPayload,
): Promise<HiredEquipmentSubmitResult> {
  const response = await fetch(`${API_URL}/hired-equipment/submissions`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) await throwApiError(response, 'Could not submit hired equipment');
  return response.json() as Promise<HiredEquipmentSubmitResult>;
}

export async function listHiredEquipmentDrafts(): Promise<HiredEquipmentDraftSummary[]> {
  const response = await fetch(`${API_URL}/hired-equipment/drafts`, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(await parseHiredEquipmentError(response, 'Failed to load drafts'));
  }
  return response.json() as Promise<HiredEquipmentDraftSummary[]>;
}

export async function getHiredEquipmentDraft(formId: string): Promise<HiredEquipmentDraftRecord> {
  const response = await fetch(`${API_URL}/hired-equipment/drafts/${encodeURIComponent(formId)}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(await parseHiredEquipmentError(response, 'Failed to open draft'));
  }
  return response.json() as Promise<HiredEquipmentDraftRecord>;
}

export async function saveHiredEquipmentDraft(
  payload: {
    anchor: HiredEquipmentDraftAnchor;
    rows: unknown[];
    internalComment?: string | null;
  },
  formId?: string | null,
): Promise<HiredEquipmentDraftRecord> {
  const path = formId
    ? `${API_URL}/hired-equipment/drafts/${encodeURIComponent(formId)}`
    : `${API_URL}/hired-equipment/drafts`;
  const response = await fetch(path, {
    method: formId ? 'PUT' : 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await parseHiredEquipmentError(response, 'Failed to save draft'));
  }
  return response.json() as Promise<HiredEquipmentDraftRecord>;
}

export async function discardHiredEquipmentDraft(formId: string): Promise<void> {
  const response = await fetch(`${API_URL}/hired-equipment/drafts/${encodeURIComponent(formId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(await parseHiredEquipmentError(response, 'Failed to discard draft'));
  }
}

export async function updateHiredEquipmentLines(
  formId: string,
  payload: {
    rows: HiredEquipmentSubmitRowPayload[];
    resubmit?: boolean;
    internalComment?: string | null;
    /** Set when the person has finished the edit. Autosave leaves this off. */
    recordHistory?: boolean;
    historyBaseline?: {
      rows: HiredEquipmentSubmitRowPayload[];
      internalComment?: string | null;
    };
  },
): Promise<{
  fields: Array<{ id: number; field: string; value: string; path: string; controlType: string; dataType: string; question: string }>;
  status?: string;
  sameDayWarnings?: HeSameDayTotal[];
}> {
  const response = await fetch(`${API_URL}/hired-equipment/submissions/${encodeURIComponent(formId)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) await throwApiError(response, 'Could not save the hired equipment lines');
  return response.json();
}

export interface HiredEquipmentFuelSurchargeRow {
  id: number;
  vehicleType: 'ON_ROAD' | 'OFF_ROAD';
  surchargePerHour: number;
  effectiveAt: string;
}

export async function listHiredEquipmentFuelSurcharges(): Promise<HiredEquipmentFuelSurchargeRow[]> {
  const response = await fetch(`${API_URL}/hired-equipment/fuel-surcharges`, {
    credentials: 'include',
  });
  if (!response.ok) {
    let message = 'Failed to load fuel surcharges';
    try {
      const body = (await response.json()) as { error?: string; details?: string };
      message = body?.error || body?.details || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return response.json() as Promise<HiredEquipmentFuelSurchargeRow[]>;
}

export interface HeVendorApprovalEmailPreview {
  formId: string;
  vendorName: string;
  vendorEmail: string | null;
  ticket: string;
  workDateLabel: string;
  serviceArea: string;
  subject: string;
  bodyText: string;
  attachmentFilename: string;
  fromEmail: string;
  fromName: string;
  replyToEmail: string;
  senderDisplayName: string;
  sendgridConfigured: boolean;
  canSend: boolean;
  cannotSendReason: string | null;
}

/** Coordinator edits from the approval email preview. */
export interface HeVendorEmailDraft {
  to: string;
  subject: string;
  bodyText: string;
}

async function parseHiredEquipmentError(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string; details?: string };
    return body?.error || body?.details || fallback;
  } catch {
    return fallback;
  }
}

export async function fetchHeVendorApprovalEmailPreview(
  formId: string,
): Promise<HeVendorApprovalEmailPreview> {
  const response = await fetch(
    `${API_URL}/hired-equipment/submissions/${encodeURIComponent(formId)}/vendor-email`,
    { credentials: 'include' },
  );
  if (!response.ok) {
    throw new Error(await parseHiredEquipmentError(response, 'Failed to load vendor email preview'));
  }
  return response.json() as Promise<HeVendorApprovalEmailPreview>;
}

export async function sendHeVendorApprovalEmail(
  formId: string,
  draft?: Partial<HeVendorEmailDraft>,
): Promise<{ sent: boolean; to: string; messageId: string | null }> {
  const response = await fetch(
    `${API_URL}/hired-equipment/submissions/${encodeURIComponent(formId)}/vendor-email`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(draft ?? {}),
    },
  );
  if (!response.ok) {
    throw new Error(await parseHiredEquipmentError(response, 'Failed to send vendor email'));
  }
  return response.json() as Promise<{ sent: boolean; to: string; messageId: string | null }>;
}

function filenameFromContentDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const quoted = header.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];
  const unquoted = header.match(/filename=([^;]+)/i);
  return unquoted?.[1]?.trim() || fallback;
}

/** H1: email preview for a submission that has not been saved yet (Operations Coordinator submit). */
export async function fetchUnsavedHeVendorEmailPreview(
  payload: HiredEquipmentSubmitPayload,
): Promise<HeVendorApprovalEmailPreview> {
  const response = await fetch(`${API_URL}/hired-equipment/submissions/preview/vendor-email`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) await throwApiError(response, 'Could not load the email preview');
  return response.json() as Promise<HeVendorApprovalEmailPreview>;
}

export async function fetchUnsavedHeVendorCopy(
  payload: HiredEquipmentSubmitPayload,
): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(`${API_URL}/hired-equipment/submissions/preview/vendor-email/copy`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) await throwApiError(response, 'Could not prepare the copy');
  return {
    blob: await response.blob(),
    filename: filenameFromContentDisposition(response.headers.get('Content-Disposition'), 'hired-equipment.html'),
  };
}

export async function fetchHeVendorApprovalEmailPdf(
  formId: string,
): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(
    `${API_URL}/hired-equipment/submissions/${encodeURIComponent(formId)}/vendor-email/pdf`,
    { credentials: 'include' },
  );
  if (!response.ok) {
    throw new Error(await parseHiredEquipmentError(response, 'Failed to download copy'));
  }
  return {
    blob: await response.blob(),
    filename: filenameFromContentDisposition(
      response.headers.get('Content-Disposition'),
      'hired-equipment.html',
    ),
  };
}
